// End-of-class graded quiz. Reuses the authenticated activity engine
// (course-activity-attempt) for taking/grading; course-class-quiz only
// orchestrates starting/closing an instance for a specific class session.
import { callFn } from "./client";

export interface QuizOption {
  id: string;
  option_text: string;
  option_text_es: string | null;
  position: number;
}

export interface QuizQuestion {
  id: string;
  prompt: string;
  prompt_es: string | null;
  question_type: string;
  difficulty: "easy" | "medium" | "hard";
  points: number;
  /** How long this question is worth, decided by the server. The client keeps
   *  no timing rule of its own — the two repos deploy independently, so a
   *  constant on both sides drifts silently. */
  seconds: number;
  options: QuizOption[];
  /** Why the correct option is right. NEVER populated by start_attempt — that
   *  would ship the answer key before question 1 is even on screen, the same
   *  leak `is_correct` is deliberately left off these options for. Optional
   *  here because it only exists once Player.tsx merges submit_attempt's
   *  `explanations` map onto a copy of this array, after the quiz is graded,
   *  for the post-quiz review list alone. */
  explanation?: string | null;
  explanation_es?: string | null;
}

export interface QuizAttempt {
  id: string;
  activity_instance_id: string;
  status: string;
  started_at: string;
  submitted_at: string | null;
  attempt_number: number;
  // Present on a resumed attempt (course-activity-attempt selects them);
  // null until grading has run.
  score_raw?: number | null;
  score_percent?: number | null;
  score_final?: number | null;
  /** Secret racer identity for the piñata race; null outside live-class quizzes. */
  racer_name?: string | null;
  racer_emoji?: string | null;
  // The resume trio, saved as the student goes so a kicked or reloaded phone
  // continues in place instead of starting over (added after 2026-08-20, when
  // three students lost their quiz mid-class to expired-token kicks).
  /** When the student tapped "Let's go" — the per-question schedule anchor. */
  clock_t0?: string | null;
  /** question_id -> selected_option_id, everything answered so far. */
  progress_answers?: Record<string, string> | null;
  /** The furthest question index reached (monotonic, server-clamped). */
  progress_position?: number | null;
}

export interface StartAttemptResponse {
  attempt: QuizAttempt;
  questions: QuizQuestion[];
  activity_instance: { id: string; ends_at: string | null; time_limit_seconds: number | null; question_count: number | null };
}

export interface SubmitAttemptResponse {
  attempt: QuizAttempt;
  score: { raw: number; total: number; percent: number; speed_bonus: number; final: number };
  /** question id -> correct option id, from the server's own grading of this
   *  submit — never from anything the client sent. This is the answer key,
   *  so it only ever arrives attached to a response that already closed the
   *  attempt out. */
  correct: Record<string, string>;
  /** question id -> explanation, in both languages. `QuizQuestion.explanation`
   *  is never populated by start_attempt (a review round found it was shipping
   *  the answer key at quiz start otherwise) — this is the only place a
   *  question's explanation ever arrives from. */
  explanations: Record<string, { explanation: string | null; explanation_es: string | null }>;
}

export function startQuizAttempt(activityInstanceId: string) {
  return callFn<StartAttemptResponse>("course-activity-attempt", {
    action: "start_attempt",
    activity_instance_id: activityInstanceId
  });
}

export function submitQuizAttempt(input: {
  attempt_id: string;
  responses: Array<{ question_id: string; selected_option_id: string }>;
  integrity?: Record<string, unknown>;
}) {
  return callFn<SubmitAttemptResponse>("course-activity-attempt", { action: "submit_attempt", ...input });
}

/** Fire-and-forget: "I'm on question `position`, answered `answered`." Moves
 *  this student's racer on the room's screen — and carries the full answer map,
 *  which is BOTH the recovery copy a kicked phone resumes from and the record
 *  the server grades from. `clock_start` marks the "Let's go" tap; since the
 *  room clock replaced the per-phone one it anchors nothing, and the server
 *  stamps `clock_t0` on the first ping it sees either way. Callers swallow
 *  failures — a dropped ping must never interrupt a student mid-quiz. */
export function reportProgress(input: {
  attempt_id: string;
  position: number;
  answered: number;
  answers?: Record<string, string>;
  clock_start?: boolean;
}) {
  return callFn<{ ok: boolean }>("course-activity-attempt", { action: "report_progress", ...input });
}

export function setQuizNameReveal(input: { attempt_id: string; revealed: boolean }) {
  return callFn<{ attempt_id: string; name_revealed: boolean }>(
    "course-activity-attempt",
    { action: "set_name_reveal", ...input }
  );
}

/** One cheer from a finished student. The server picks who receives it and
 *  enforces a 20-second cooldown. */
export function cheerRacer(input: { attempt_id: string }) {
  return callFn<{ ok: boolean; reason?: string; to?: { racer_name: string; racer_emoji: string } }>(
    "course-activity-attempt",
    { action: "cheer", ...input }
  );
}

// ---------------------------------------------------------------- instructor
/** The time_limit_seconds includes the professor's one-minute cushion. There is
 *  no question_count: the deal is the fixed 4/3/3 quota, and the server sizes
 *  the instance from it so the clock, the rounds and the piñata's denominator
 *  cannot disagree with the questions actually dealt. */
export function startClassQuiz(input: { class_session_id: string; content_slug: string; time_limit_seconds?: number }) {
  return callFn<{ instance_id: string; reused: boolean }>("course-class-quiz", { action: "start", ...input });
}

/** The running quiz (if any) plus the last finished one, kept separate so a
 *  closed quiz never hides the "start" control — a class can run more than one. */
export function currentClassQuiz(input: { class_session_id: string; content_slug: string }) {
  return callFn<{
    instance_id: string | null;
    state: string | null;
    last_closed_instance_id: string | null;
  }>("course-class-quiz", { action: "current", ...input });
}

export function closeClassQuiz(activityInstanceId: string) {
  return callFn<{ instance_id: string; state: string }>("course-class-quiz", { action: "close", activity_instance_id: activityInstanceId });
}

export interface QuizStatus {
  instance_id: string;
  state: string;
  ends_at: string | null;
  question_count: number | null;
  enrolled: number;
  /** Students in the room today — the roster is who COULD have come. The
   *  completeness message has to speak in this number. */
  present: number;
  started: number;
  submitted: number;
  closed_reason: "time" | "everyone" | null;
  average_score: number | null;
}

export function classQuizStatus(activityInstanceId: string) {
  return callFn<QuizStatus>("course-class-quiz", { action: "status", activity_instance_id: activityInstanceId });
}

export interface QuizAttemptSummary {
  profile_id: string;
  name: string;
  student_identifier: string | null;
  status: string;
  score_percent: number | null;
  score_final: number | null;
  submitted_at: string | null;
}

export function classQuizSummary(classSessionId: string) {
  return callFn<{ attempts: QuizAttemptSummary[] }>("course-class-quiz", { action: "summary", class_session_id: classSessionId });
}

export interface PodiumEntry {
  rank: number;
  profile_id: string;
  student_identifier: string | null;
  score_final: number | null;
  name_revealed: boolean;
  /** Present only when that student opted in. The server withholds it
   *  otherwise — this is never a name the client is trusted to hide. */
  name: string | null;
}

export function classQuizPodium(input: { class_session_id: string }) {
  return callFn<{ instance_id: string | null; entries: PodiumEntry[] }>(
    "course-class-quiz",
    { action: "podium", ...input }
  );
}

export interface RaceRacer {
  racer_name: string;
  racer_emoji: string;
  /** Height on the climb — correctness plus speed. Never a grade. */
  candy: number;
  /** Size on the climb — cumulative, so a racer never shrinks. */
  correct_count: number;
  finished: boolean;
  finish_place: number | null;
}

/** The room's round window, server-decided. The screen counts down against
 *  these deadlines rather than a clock of its own — the phones read the same
 *  window out of course-pulse, and the two repos deploy independently. */
export interface RaceRound {
  index: number;
  phase: "answering" | "break" | "done";
  answer_ends_at: string;
  break_ends_at: string;
}

export interface RaceCheer {
  from_name: string;
  from_emoji: string;
  to_name: string;
  to_emoji: string;
  at: string;
}

export interface RaceStatus {
  instance_id: string;
  state: string;
  ends_at: string | null;
  question_count: number | null;
  present: number;
  started: number;
  submitted: number;
  closed_reason: "time" | "everyone" | null;
  round: RaceRound | null;
  /** How many students got the round that just closed right — the flash beat. */
  round_correct: number;
  /** Damage is correct answers, not answers given. `correct` and `total` are
   *  the room's, not one student's. */
  pinata: { name: string; correct: number; total: number; percent: number; burst: boolean };
  racers: RaceRacer[];
  cheers: RaceCheer[];
  cheers_total: number;
}

/** The room's screen, in one call: counts, racers by secret name, the piñata. */
export function classQuizRace(activityInstanceId: string) {
  return callFn<RaceStatus>("course-class-quiz", { action: "race", activity_instance_id: activityInstanceId });
}
